<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { i18n } from '@/i18n';
import SvgLoader from '@/utils/SvgLoader.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzTableLoadingState from '@/corePagesModule/components/ezTable/EzTableLoadingState.vue';
import EzTableErrorState from '@/corePagesModule/components/ezTable/EzTableErrorState.vue';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import { IForecastAccuracyHistory } from '../../domains/entities/ForecastAccuracy';
import { ACCURACY_THRESHOLDS } from '../../constants/forecastAccuracy';
import TopSlideDownModal from '@/corePagesModule/components/TopSlideDownModal.vue';

/**
 * ForecastAccuracyHistoryModal コンポーネントのプロパティ
 */
interface Props {
  /** モーダルが開いているかどうか */
  isOpen: boolean;
  /** 履歴データ */
  historyData: IForecastAccuracyHistory | null;
  /** ローディング状態 */
  loadingStatus: LoadingStatus;
}

/**
 * ForecastAccuracyHistoryModal コンポーネントのイベント
 */
interface Emits {
  /** モーダルを閉じる */
  close: [];
  /** 期間変更 */
  'change-period': [period: 1 | 2 | 3];
  /** リトライ */
  retry: [];
}

const props = defineProps<Props>();
const emit = defineEmits(['close', 'change-period', 'retry']);

// i18n関数
const t = i18n.t;

// 選択中の期間
const selectedPeriod = ref<1 | 2 | 3>(1);

/**
 * テーブル用データを転置形式で生成
 * @description 月別データを項目別行形式に変換
 */
const tableData = computed(() => {
  if (!props.historyData?.historyData) return [];

  // 過去1年分のデータを古い月から新しい月の順番で取得
  const sortedData = [...props.historyData.historyData]
    .sort((a, b) => new Date(a.yearMonth).getTime() - new Date(b.yearMonth).getTime())
    .slice(0, 12);

  return [
    {
      label: t('販売実績'),
      type: 'salesResult',
      data: sortedData.map((item) => ({
        value: item.salesResult,
        yearMonth: item.yearMonth,
      })),
    },
    {
      label: t('内示'),
      type: 'forecast',
      data: sortedData.map((item) => ({
        value: item.forecast,
        yearMonth: item.yearMonth,
      })),
    },
    {
      label: t('内示正答率'),
      type: 'accuracyRate',
      data: sortedData.map((item) => ({
        value: item.accuracyRate,
        yearMonth: item.yearMonth,
      })),
    },
  ];
});

/**
 * テーブルヘッダー定義（動的に月列を生成）
 */
const tableHeaders = computed(() => {
  if (!props.historyData?.historyData) return [{ key: 'label', label: '' }];

  // 過去1年分のデータを古い月から新しい月の順番でソート
  const sortedData = [...props.historyData.historyData]
    .sort((a, b) => new Date(a.yearMonth).getTime() - new Date(b.yearMonth).getTime())
    .slice(0, 12);

  const headers = [{ key: 'label', label: '' }]; // 最初の列は空白

  sortedData.forEach((item) => {
    headers.push({
      key: item.yearMonth,
      label: formatYearMonthHeader(item.yearMonth),
    });
  });

  return headers;
});

/**
 * grid-template-columnsを動的に生成
 */
const gridTemplateColumns = computed((): string[] => {
  const columnCount = tableHeaders.value.length - 1; // 最初の列はラベル用なので除外
  // 各列の幅を90pxに設定
  const gridTemplateColumns = Array(columnCount).fill('90px');
  return ['200px', ...gridTemplateColumns]; // 最初の列はラベル用に200px
});

/**
 * ヘッダー用年月フォーマット（YYYY/MM形式）
 * @param yearMonth - YYYY-MM形式の年月
 * @returns YYYY/MM形式の年月文字列
 */
const formatYearMonthHeader = (yearMonth: string): string => {
  return yearMonth.replace('-', '/');
};

/**
 * 数値のフォーマット
 * @param value - 数値
 * @returns カンマ区切りの数値文字列
 */
const formatNumber = (value: number): string => {
  return value.toLocaleString();
};

/**
 * 正答率に応じたスタイルを取得
 * @param rate - 正答率
 * @returns スタイルオブジェクト
 */
const getAccuracyStyle = (rate: number) => {
  if (rate <= ACCURACY_THRESHOLDS.CRITICAL) {
    return {
      backgroundColor: 'var(--color-semantic-error-subdued)',
      color: 'var(--color-semantic-error)',
      fontWeight: 'bold',
    };
  } else if (rate > ACCURACY_THRESHOLDS.CRITICAL && rate <= ACCURACY_THRESHOLDS.WARNING) {
    return {
      color: 'var(--color-warning)',
      fontWeight: 'bold',
    };
  }
  return { fontWeight: 'bold' };
};

/**
 * モーダルを閉じる
 */
const handleClose = () => {
  emit('close');
};

/**
 * 期間変更処理
 */
const handlePeriodChange = () => {
  emit('change-period', selectedPeriod.value);
};

/**
 * リトライ処理
 */
const handleRetry = () => {
  emit('retry');
};

// 履歴データの変更を監視して期間を同期
watch(
  () => props.historyData?.selectedPeriod,
  (newPeriod) => {
    if (newPeriod) {
      selectedPeriod.value = newPeriod as 1 | 2 | 3;
    }
  },
  { immediate: true }
);

// モーダルが開かれたときに期間を1にリセット
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      selectedPeriod.value = 1;
    }
  },
  { immediate: true }
);

const modalStyle = {
  display: 'grid',
  alignItems: 'center',
  justifyContent: 'center',
  gridTemplateColumns: 'minmax(calc(200px + 12 * 90px), auto)',
};
</script>
<template>
  <TopSlideDownModal :is-open="isOpen" :style="modalStyle">
    <!-- ヘッダー -->
    <div class="forecast-accuracy-modal__header">
      <h3 class="forecast-accuracy-modal__title">
        {{ t('内示正答率履歴(モーダルタイトル)') }}
      </h3>
      <div class="forecast-accuracy-modal__controls">
        <!-- 期間選択 -->
        <select v-model="selectedPeriod" class="forecast-accuracy-modal__period-select" @change="handlePeriodChange">
          <option value="1">{{ t('1ヶ月') }}</option>
          <option value="2">{{ t('2ヶ月') }}</option>
          <option value="3">{{ t('3ヶ月') }}</option>
        </select>

        <!-- 平均正答率表示 -->
        <div v-if="historyData" class="forecast-accuracy-modal__average">
          {{ t('平均:') }}
          <span class="forecast-accuracy-modal__average-number">
            {{ formatNumber(historyData.averageAccuracy) }}
          </span>
          %
        </div>

        <!-- 閉じるボタン -->
        <button class="forecast-accuracy-modal__close-btn" @click="handleClose">
          <SvgLoader name="close" />
        </button>
      </div>
    </div>

    <!-- コンテンツ -->
    <div class="forecast-accuracy-modal__content">
      <!-- データ表示 -->
      <div
        class="forecast-accuracy-modal__table-container"
        :class="{ 'forecast-accuracy-modal__table-container--border': loadingStatus.type === 'success' }"
      >
        <EzTable
          :items="tableData"
          :grid-template-columns="gridTemplateColumns"
          :loading-status="loadingStatus"
          class="forecast-accuracy-history-table"
        >
          <template #header>
            <EzHeader v-for="header in tableHeaders" :key="header.key" class="forecast-accuracy-table__header">
              {{ header.label }}
            </EzHeader>
          </template>

          <template #body>
            <!-- 各行データ -->
            <EzTableRow
              v-for="item in tableData"
              :key="item.type"
              :grid-template-columns="gridTemplateColumns"
              class="forecast-accuracy-table__row"
            >
              <!-- 項目名列 -->
              <EzCell class="forecast-accuracy-table__cell forecast-accuracy-table__cell--label">
                {{ item.label }}
              </EzCell>

              <!-- 各月のデータ列 -->
              <EzCell
                v-for="dataPoint in item.data"
                :key="dataPoint.yearMonth"
                class="forecast-accuracy-table__cell forecast-accuracy-table__cell--number"
                is-number
                :style="item.type === 'accuracyRate' ? getAccuracyStyle(dataPoint.value) : {}"
              >
                <template v-if="item.type === 'accuracyRate'"> {{ dataPoint.value }}% </template>
                <template v-else>
                  {{ formatNumber(dataPoint.value) }}
                </template>
              </EzCell>
            </EzTableRow>
          </template>

          <template #loading>
            <!-- ローディング状態 -->
            <div class="forecast-accuracy-modal__loading">
              <EzTableLoadingState />
            </div>
          </template>

          <template #error>
            <!-- エラー状態 -->
            <div class="forecast-accuracy-modal__error">
              <EzTableErrorState @retry="handleRetry" />
              <p class="forecast-accuracy-modal__error-message">
                {{ t('履歴の取得に失敗しました') }}
              </p>
            </div>
          </template>
        </EzTable>
      </div>
    </div>
  </TopSlideDownModal>
</template>

<style lang="scss" scoped>
.forecast-accuracy-modal {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 12px;
    border-bottom: 1px solid var(--color-border-default);
    background: var(--color-background-subtle);
  }

  &__title {
    font-family: Noto Sans JP;
    font-weight: 700;
    font-style: Bold;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0%;
    vertical-align: middle;
    color: $textDefault;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__period-select {
    padding: 4px 8px;
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
    font-family: Noto Sans JP;
    font-weight: 400;
    font-style: Regular;
    font-size: 14px;
    line-height: 100%;
    letter-spacing: 0%;
    vertical-align: middle;
    color: $textDefault;

    background: white;

    &:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px var(--color-primary-subdued);
    }
  }

  &__average {
    font-size: 14px;
    font-weight: 600;
    color: $textDefault;

    background: var(--color-primary-subdued);
    padding: 4px 12px;
    font-family: Noto Sans JP;
    font-weight: 400;
    font-style: Regular;
    font-size: 14px;
    line-height: 100%;
    letter-spacing: 0%;
    vertical-align: middle;

    &-number {
      font-family: Roboto;
      font-weight: 700;
      font-style: Bold;
      font-size: 26px;
      line-height: 100%;
      letter-spacing: 0%;
      vertical-align: middle;
      color: $semanticPrimary;
    }
  }

  &__close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: var(--color-text-secondary);
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
      background: var(--color-background-hover);
      color: var(--color-text-primary);
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }

  &__content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__loading,
  &__error {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  &__error-message {
    margin-top: 8px;
    color: var(--color-semantic-error);
    font-size: 14px;
    text-align: center;
  }

  &__table-container {
    flex: 1;
    overflow: auto;
    margin: 8px 16px;

    &--border {
      border: 1px solid $borderDefault;
      border-radius: 4px;
    }
  }

  &__table {
    overflow-x: hidden;
  }
}

.forecast-accuracy-history-table {
  overflow-x: hidden;

  :deep(.ez-table) {
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
  }
}

.forecast-accuracy-table {
  &__header {
    background: var(--color-background-subtle);
    font-weight: 600;
    text-align: center;
    padding: 8px 6px;
    border-bottom: 1px solid var(--color-border-default);
    font-family: Noto Sans JP;
    font-weight: 700;
    font-style: Bold;
    font-size: 14px;
    line-height: 100%;
    letter-spacing: 0%;
    text-align: center;
    vertical-align: middle;
    color: $textDefault;
    background-color: $surfaceDefault;
  }

  &__row {
    color: $textSecondary;
    border-bottom: 1px solid $borderDefault;

    &:last-child {
      border-bottom: none;
    }
  }

  &__cell {
    padding: 8px 6px;
    text-align: center;
    border-bottom: 1px solid var(--color-border-subtle);
    font-family: Noto Sans JP;
    font-weight: 400;
    font-style: Regular;
    font-size: 14px;
    line-height: 130%;
    letter-spacing: 0%;
    text-align: center;
    vertical-align: middle;

    &--label {
      text-align: left;
      font-weight: 600;
      background: var(--color-background-subtle);
    }

    &--number {
      font-feature-settings: 'tnum';
      font-variant-numeric: tabular-nums;
    }
  }
}
</style>
